import {
  Avatar,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import {
  PlusOutlined,
  RightOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AccountManagementCustomerService from "../services/AccountManagementCustomerService";
import {
  IAccountManagementAssignableMember,
  IAccountManagementCustomer,
} from "../types/AccountManagementCustomer";
import { can, convertDate, openNotification } from "../utils";
import CopyToClipboard from "react-copy-to-clipboard";
import { CopyOutlined } from "@ant-design/icons";
import _ from "lodash";
import dayjs from "dayjs";
import PnlFilterBar from "../components/AccountManagementCustomer/PnlFilterBar";
import {
  currentMonthRange,
  formatPeriodLabel,
} from "../components/AccountManagementCustomer/pnlFilterUtils";
import "./AccountManagementCustomerList.css";

const PLACEHOLDER = "—";
const EMAIL_TRUNCATE_AT = 30;
const NAME_TRUNCATE_AT = 26;

const truncateText = (value: string, maxLength: number) => {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)}…`;
};

const AVATAR_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#059669",
  "#0891b2",
];

const getInitials = (row: IAccountManagementCustomer) => {
  const source = row.fullName?.trim() || row.email;
  const parts = source.split(/\s+|@/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const getAvatarColor = (seed: string) => {
  const code = seed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const FINANCIAL_COLUMNS = [
  { key: "orderCount", title: "Sipariş", width: 64 },
  { key: "revenue", title: "Ciro", width: 72 },
  { key: "warehouseCost", title: "Ara Depo", width: 78 },
  { key: "productCost", title: "Ürün Mal.", width: 78 },
  { key: "totalCost", title: "Toplam Mal.", width: 86 },
  { key: "profit", title: "Kar", width: 70 },
  { key: "profitPercent", title: "Marj", width: 62 },
] as const;

type FinancialKey = (typeof FINANCIAL_COLUMNS)[number]["key"];

/** Tutarlar küsurat olmadan gösterilir; kolonlar dar ve satır yoğunluğu yüksek. */
const compactMoney = (value: number) =>
  `$${Math.round(value).toLocaleString("en-US")}`;

const renderMetric = (row: IAccountManagementCustomer, key: FinancialKey) => {
  const value = row[key];
  if (value == null || (key === "orderCount" && value === 0)) {
    return <span className="acm-num acm-num--muted">{PLACEHOLDER}</span>;
  }
  if (key === "orderCount") {
    return <span className="acm-num">{value}</span>;
  }
  if (key === "profitPercent") {
    return (
      <span className={`acm-num${value < 0 ? " acm-num--neg" : " acm-num--pos"}`}>
        {value.toFixed(0)}%
      </span>
    );
  }
  const signClass =
    key === "profit" ? (value < 0 ? " acm-num--neg" : " acm-num--pos") : "";
  return (
    <Tooltip title={value.toLocaleString("en-US", { minimumFractionDigits: 2 })}>
      <span className={`acm-num${signClass}`}>{compactMoney(value)}</span>
    </Tooltip>
  );
};

export const AccountManagementCustomerList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lastCurrentPage = location.state?.currentPage ?? 0;
  const lastPageSize = location.state?.pageSize ?? 20;
  const lastSortByField = location.state?.sortByField ?? "createdAt";
  const lastSortByOrder = location.state?.sortByOrder ?? "desc";
  const lastSearchText = location.state?.searchText ?? "";

  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(lastSearchText);
  const [currentPage, setCurrentPage] = useState(lastCurrentPage);
  const [pageSize, setPageSize] = useState(lastPageSize);
  const [sortByField, setSortByField] = useState(lastSortByField);
  const [sortByOrder, setSortByOrder] = useState(lastSortByOrder);
  const [customers, setCustomers] = useState<IAccountManagementCustomer[]>([]);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [periodFilter, setPeriodFilter] = useState<{ periodStart?: string; periodEnd?: string }>(
    () => {
      const month = currentMonthRange();
      return { periodStart: month.from, periodEnd: month.to };
    }
  );
  const [form] = Form.useForm();
  const [assignableMembers, setAssignableMembers] = useState<IAccountManagementAssignableMember[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [bulkAssignUserIds, setBulkAssignUserIds] = useState<number[]>([]);
  const [assigning, setAssigning] = useState(false);

  const canAssign = can("account-management-customers:assign");
  const periodLabel = formatPeriodLabel(periodFilter.periodStart, periodFilter.periodEnd);

  const navigationState = { currentPage, pageSize, sortByField, sortByOrder, searchText };

  const buildParam = useCallback(() => {
    const params = new URLSearchParams();
    params.set("page", String(currentPage));
    params.set("size", String(pageSize));
    params.set("sort", sortByField);
    params.set("direction", sortByOrder);
    if (searchText) params.set("search", searchText);
    if (periodFilter.periodStart) params.set("periodStart", periodFilter.periodStart);
    if (periodFilter.periodEnd) params.set("periodEnd", periodFilter.periodEnd);
    return `?${params.toString()}`;
  }, [currentPage, pageSize, sortByField, sortByOrder, searchText, periodFilter]);

  const loadCustomers = useCallback(() => {
    setLoading(true);
    const param = buildParam();
    localStorage.setItem("accountManagementFilterText", param);
    AccountManagementCustomerService.getAll(param)
      .then((response) => {
        setCustomers(response.data.data);
        setTotalItems(response.data.totalElements);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [buildParam]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (!canAssign) return;
    AccountManagementCustomerService.getAssignableMembers()
      .then((response) => setAssignableMembers(response.data))
      .catch(console.error);
  }, [canAssign]);

  const memberLabel = (member: IAccountManagementAssignableMember) => {
    const fullName = [member.firstName, member.lastName].filter(Boolean).join(" ").trim();
    return fullName || member.username;
  };

  const memberOptions = useMemo(
    () =>
      assignableMembers.map((member) => ({
        value: member.id,
        label: memberLabel(member),
      })),
    [assignableMembers]
  );

  const handleAssignMember = async (customerId: number, userIds: number[]) => {
    setAssigning(true);
    try {
      await AccountManagementCustomerService.assignMember(customerId, userIds);
      openNotification(
        "success",
        "Success",
        userIds.length ? "Üyeler atandı" : "Atamalar kaldırıldı"
      );
      loadCustomers();
    } catch (e) {
      console.error(e);
    } finally {
      setAssigning(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedRowKeys.length) return;
    setAssigning(true);
    try {
      await Promise.all(
        selectedRowKeys.map((id) =>
          AccountManagementCustomerService.assignMember(id, bulkAssignUserIds)
        )
      );
      openNotification(
        "success",
        "Success",
        bulkAssignUserIds.length
          ? `${selectedRowKeys.length} kayıt atandı`
          : `${selectedRowKeys.length} kayıttan atamalar kaldırıldı`
      );
      setSelectedRowKeys([]);
      setBulkAssignUserIds([]);
      loadCustomers();
    } catch (e) {
      console.error(e);
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [periodFilter.periodStart, periodFilter.periodEnd]);

  const debouncedSearch = useMemo(
    () =>
      _.debounce((value: string) => {
        setSearchText(value);
        setCurrentPage(0);
      }, 400),
    []
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const activeCount = useMemo(
    () => customers.filter((c) => c.packageActive && !c.hi5SyncError).length,
    [customers]
  );

  const handleTableChange: TableProps<IAccountManagementCustomer>["onChange"] = (
    pagination,
    _filters,
    sorter: any
  ) => {
    if (pagination?.current) {
      setCurrentPage(pagination.current - 1);
    }
    if (pagination?.pageSize) {
      setPageSize(pagination.pageSize);
    }
    if (sorter?.field) {
      setSortByField(sorter.field);
      setSortByOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await AccountManagementCustomerService.create(values);
      openNotification("success", "Success", "Record created");
      form.resetFields();
      setCreateDrawerOpen(false);
      loadCustomers();
    } catch (e) {
      console.error(e);
    }
  };

  const goToDetail = (id: number) => {
    navigate(`/account-management-customers/detail/${id}`, { state: navigationState });
  };

  const renderStatus = (active?: boolean, syncError?: boolean) => {
    if (syncError) {
      return (
        <span className="acm-status acm-status--unknown">
          <span className="acm-status-dot" />
          Bilinmiyor
        </span>
      );
    }
    if (active) {
      return (
        <span className="acm-status acm-status--active">
          <span className="acm-status-dot" />
          Aktif
        </span>
      );
    }
    return (
      <span className="acm-status acm-status--inactive">
        <span className="acm-status-dot" />
        Pasif
      </span>
    );
  };

  const columns: ColumnsType<IAccountManagementCustomer> = [
    {
      title: "Müşteri",
      dataIndex: "email",
      key: "email",
      sorter: true,
      width: 222,
      render: (_email, row) => {
        const displayName = row.fullName || "—";
        const displayEmail = truncateText(row.email, EMAIL_TRUNCATE_AT);
        return (
          <div className="acm-customer">
            <Avatar
              size={28}
              className="acm-avatar"
              style={{ backgroundColor: getAvatarColor(row.email) }}
            >
              {getInitials(row)}
            </Avatar>
            <div className="acm-customer-info">
              <div
                className="acm-customer-name"
                title={displayName.length > NAME_TRUNCATE_AT ? displayName : undefined}
              >
                {truncateText(displayName, NAME_TRUNCATE_AT)}
              </div>
              <div className="acm-customer-email">
                <span title={row.email}>{displayEmail}</span>
                <CopyToClipboard text={row.email}>
                  <CopyOutlined
                    className="acm-copy-icon"
                    onClick={() => openNotification("success", "Copied to clipboard", row.email)}
                  />
                </CopyToClipboard>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "ML ID",
      dataIndex: "mongerlabUserId",
      key: "mongerlabUserId",
      sorter: true,
      width: 68,
      align: "center",
      render: (v) => <span className="acm-id-badge">{v ?? PLACEHOLDER}</span>,
    },
    {
      title: "Atanan Üye",
      dataIndex: "assignedMembers",
      key: "assignedMembers",
      width: canAssign ? 200 : 160,
      render: (_v, row) => {
        if (canAssign) {
          return (
            <Select
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="—"
              style={{ width: "100%" }}
              loading={assigning}
              optionFilterProp="label"
              value={row.assignedMembers?.map((m) => m.id) ?? []}
              options={memberOptions}
              onClick={(e) => e.stopPropagation()}
              onChange={(value) => handleAssignMember(row.id, value ?? [])}
            />
          );
        }
        if (!row.assignedMembers?.length) {
          return <span>{PLACEHOLDER}</span>;
        }
        return (
          <span className="acm-assigned-members">
            {row.assignedMembers.map((member) => (
              <Tag key={member.id}>{memberLabel(member)}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: "Paket",
      dataIndex: "packageActive",
      key: "packageActive",
      width: 114,
      render: (_v, row) => (
        <div className="acm-package">
          {renderStatus(row.packageActive, row.hi5SyncError)}
          <span className="acm-package-date">
            {row.hi5SyncError ? PLACEHOLDER : row.packageEndDate || PLACEHOLDER}
          </span>
        </div>
      ),
    },
    {
      title: (
        <span className="acm-group-title" title="Seçili dönem finansal özeti">
          Finansal Özet ({periodLabel})
        </span>
      ),
      key: "financial",
      className: "acm-group-cell",
      children: FINANCIAL_COLUMNS.map(({ key, title, width }) => ({
        title,
        dataIndex: key,
        key,
        width,
        align: "right" as const,
        className: "acm-group-cell",
        render: (_v: unknown, row: IAccountManagementCustomer) => renderMetric(row, key),
      })),
    },
    {
      title: "Güncelleme",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      width: 90,
      render: (v, row) => (
        <Tooltip title={`Oluşturma: ${convertDate(row.createdAt)}`}>
          <span className="acm-date">{convertDate(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "action",
      width: 40,
      align: "center",
      render: (_v, row) => (
        <Button
          type="text"
          className="acm-action-btn"
          icon={<RightOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            goToDetail(row.id);
          }}
        />
      ),
    },
  ];

  return (
    <div className="acm-page">
      <div className="acm-hero">
        <div className="acm-hero-text">
          <h1>Account Management</h1>
          <p>Amazon hesap kayıtları, paket durumu ve performans metrikleri</p>
        </div>
      </div>

      <div className="acm-stats">
        <div className="acm-stat-card">
          <div className="acm-stat-label">Toplam Kayıt</div>
          <div className="acm-stat-value">{loading ? "…" : totalItems}</div>
        </div>
        <div className="acm-stat-card">
          <div className="acm-stat-label">Aktif Paket (sayfa)</div>
          <div className="acm-stat-value">{loading ? "…" : activeCount}</div>
        </div>
        <div className="acm-stat-card">
          <div className="acm-stat-label">Sayfa Boyutu</div>
          <div className="acm-stat-value">{pageSize}</div>
        </div>
      </div>

      <div className="acm-panel">
        <PnlFilterBar mode="list" filter={periodFilter} onChange={setPeriodFilter} />
        <div className="acm-panel-toolbar">
          <div className="acm-panel-toolbar-left">
            <strong>{totalItems}</strong> kayıt listeleniyor
          </div>
          <div className="acm-panel-toolbar-right">
            {canAssign && selectedRowKeys.length > 0 && (
              <>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  maxTagCount="responsive"
                  placeholder="Üye seç"
                  style={{ minWidth: 220 }}
                  optionFilterProp="label"
                  value={bulkAssignUserIds}
                  options={memberOptions}
                  onChange={(value) => setBulkAssignUserIds(value ?? [])}
                />
                <Button loading={assigning} onClick={handleBulkAssign}>
                  Toplu Ata ({selectedRowKeys.length})
                </Button>
              </>
            )}
            <Input
              className="acm-search"
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="E-posta, ad veya ML ID ara…"
              defaultValue={lastSearchText}
              onChange={(e) => debouncedSearch(e.target.value)}
            />
            {can("account-management-customers:create") && (
              <Button
                type="primary"
                className="acm-add-btn"
                icon={<PlusOutlined />}
                onClick={() => setCreateDrawerOpen(true)}
              >
                Yeni Kayıt
              </Button>
            )}
          </div>
        </div>

        <div className="acm-table-wrap">
          <Table
            className="acm-table"
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={customers}
            rowSelection={
              canAssign
                ? {
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys as number[]),
                  }
                : undefined
            }
            onChange={handleTableChange}
            tableLayout="fixed"
            scroll={{ x: canAssign ? 1244 : 1084 }}
            pagination={{
              current: currentPage + 1,
              pageSize,
              total: totalItems,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
            }}
            onRow={(row) => ({
              className: "acm-row-clickable",
              onDoubleClick: () => goToDetail(row.id),
            })}
          />
        </div>
      </div>

      <Drawer
        title="Yeni Kayıt"
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        width={420}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="email"
            label="E-posta"
            rules={[{ required: true, type: "email", message: "Geçerli e-posta gerekli" }]}
          >
            <Input placeholder="ornek@mongerlab.com" />
          </Form.Item>
          <Form.Item name="fullName" label="Ad Soyad">
            <Input placeholder="Ad Soyad" />
          </Form.Item>
          <Form.Item name="mongerlabUserId" label="Mongerlab User ID">
            <InputNumber style={{ width: "100%" }} placeholder="12345" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block>
            Kaydet
          </Button>
        </Form>
      </Drawer>
    </div>
  );
};

export default AccountManagementCustomerList;
